import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PencilIcon, ImageIcon } from "lucide-react";
import { useState } from "react";
import { ProviderMediaDialog } from "./ProviderMediaDialog";

interface Provider {
  provider_id: number;
  name: string;
  country: string;
  city: string;
  street: string;
  postcode: string;
  website?: string;
  phone_number: string;
  description?: string;
  is_active: boolean;
  certified: boolean;
}

interface ProviderListProps {
  providers: Provider[];
  onEdit: (provider: Provider) => void;
}

export function ProviderList({ providers, onEdit }: ProviderListProps) {
  const [selectedProviderId, setSelectedProviderId] = useState<number | null>(null);

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Location</TableHead>
            <TableHead>Contact</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Certified</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {providers?.map((provider) => (
            <TableRow key={provider.provider_id}>
              <TableCell className="font-medium">
                {provider.name}
                {provider.website && (
                  <a
                    href={provider.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-muted-foreground ml-2 hover:underline"
                  >
                    website ↗
                  </a>
                )}
              </TableCell>
              <TableCell>
                {provider.city}, {provider.country}
                <br />
                <span className="text-sm text-muted-foreground">
                  {provider.street}, {provider.postcode}
                </span>
              </TableCell>
              <TableCell>
                <div className="text-sm">{provider.phone_number}</div>
              </TableCell>
              <TableCell>
                <Badge variant={provider.is_active ? "default" : "secondary"}>
                  {provider.is_active ? "Active" : "Inactive"}
                </Badge>
              </TableCell>
              <TableCell>
                <Badge variant={provider.certified ? "success" : "secondary"}>
                  {provider.certified ? "Certified" : "Not Certified"}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => onEdit(provider)}
                  >
                    <PencilIcon className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSelectedProviderId(provider.provider_id)}
                  >
                    <ImageIcon className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <ProviderMediaDialog
        providerId={selectedProviderId!}
        isOpen={selectedProviderId !== null}
        onClose={() => setSelectedProviderId(null)}
      />
    </div>
  );
}